/**
 * useLiveNowBadge
 *
 * Polls the live-breaks endpoint and mirrors the "LIVE NOW" count
 * onto the iOS app icon as a red badge. Refreshes:
 *   • on mount (app cold start)
 *   • every 60s while the app is in the foreground
 *   • on App lifecycle 'resume' (user switches back to the app)
 *
 * Silent no-op on web — the helper inside `nativeBridge.setAppBadge`
 * guards on Capacitor presence.
 */
import { useEffect } from 'react';
import axios from 'axios';
import { setAppBadge, isNative } from '../utils/nativeBridge';

const POLL_MS = 60 * 1000;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

async function fetchLiveCount() {
  try {
    const res = await axios.get(`${API_BASE}/live-breaks/?status=live`, {
      timeout: 8000,
    });
    const list = Array.isArray(res.data) ? res.data : [];
    return list.length;
  } catch {
    return null;
  }
}

export default function useLiveNowBadge() {
  useEffect(() => {
    // Only run on native — saves a poll on web.
    if (!isNative()) return undefined;

    let cancelled = false;
    let appResumeCleanup = () => {};
    let intervalId = null;

    const refresh = async () => {
      const count = await fetchLiveCount();
      if (cancelled || count === null) return;
      setAppBadge(count);
    };

    // Initial fetch + interval
    refresh();
    intervalId = setInterval(refresh, POLL_MS);

    // Refresh on app foreground resume
    import('@capacitor/app')
      .then(({ App }) => {
        const listener = App.addListener('appStateChange', (state) => {
          if (state.isActive) refresh();
        });
        appResumeCleanup = () => {
          try {
            listener.remove();
          } catch {
            /* silent */
          }
        };
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      appResumeCleanup();
    };
  }, []);
}
